import { createNanoEvents } from 'nanoevents';
import MegaSDK from '@tencent/mega-sdk';
import { EventEmitter } from '@tencent/mega-sdk-protocols';

const DEBUG_TAG = '[PPTWcjsPlayerEventsManager]';
const CREATE_WCJS_PLAYER_SUCCESS = 'CREATE_WCJS_PLAYER_SUCCESS';
const SYNC_EVENTS_TIMEOUT = 5000;

/**
 * player events统一管理(挂载windows)
 * 若无需兼容多窗口，可直接使用MegaPlayer
 */
export class PlayerEventsManager {
    /**
     * 主窗口主动创建playerEvents挂载在window上
     * 并postMessage派发事件
     * @param playerEventsId 播放器实例绑定的唯一id
     */
    public static async createEvents(playerEventsId: string): Promise<EventEmitter> {
        if (!(window as any).MEGA_PLAYER_EVENTS) {
            (window as any).MEGA_PLAYER_EVENTS = new Map();
        }
        if (!(window as any).MEGA_PLAYER_EVENTS.has(playerEventsId)) {
            const events = createNanoEvents();
            const player = await MegaSDK.Player.createPlayer();
            player.syncEvents(events);
            (window as any).MEGA_PLAYER_EVENTS.set(playerEventsId, {
                events,
            });
            // 向副窗口postMessage通知
            window.postMessage({
                type: CREATE_WCJS_PLAYER_SUCCESS,
                id: playerEventsId,
            }, '*');
        }
        const eventsData = (window as any).MEGA_PLAYER_EVENTS.get(playerEventsId);
        window.log.info(`${DEBUG_TAG} createEvents playerEventsId: ${playerEventsId}`);
        return eventsData?.events;
    }

    /**
     * 调用方(副窗口未获取到需等待主窗口派发创建成功事件)全局获取
     * @param id 播放器实例绑定的唯一id
     */
    public static async getEvents(playerEventsId: string): Promise<EventEmitter | undefined> {
        try {
            if (!window.opener) {
                throw new Error('window.opener is not exist');
            }
            // 副窗口events不存在，需等待主窗口创建成功通知
            if (!(window.opener as any).MEGA_PLAYER_EVENTS?.has(playerEventsId)) {
                const listenerTimer = setTimeout(() => {
                    throw new Error('get events timeout');
                }, SYNC_EVENTS_TIMEOUT);
                const mainWindowPromise: Promise<void> = new Promise((resolve) => {
                    const listener = (event) => {
                        if (event?.data?.type !== CREATE_WCJS_PLAYER_SUCCESS || event?.data?.id !== playerEventsId) {
                            return;
                        }
                        clearTimeout(listenerTimer);
                        window.opener.removeEventListener('message', listener);
                        resolve();
                    };
                    window.opener.addEventListener('message', listener);
                });
                await mainWindowPromise;
            }
            const eventsData = (window.opener as any).MEGA_PLAYER_EVENTS.get(playerEventsId);
            window.log.info(`${DEBUG_TAG} getEvents playerEventsId: ${playerEventsId}`);
            return eventsData?.events;
        } catch (e) {
            window.log.error(`${DEBUG_TAG} getEvents error: ${e}`);
        }
    }

    public static destroyEvents(playerEventsId: string): void {
        (window as any).MEGA_PLAYER_EVENTS?.delete?.(playerEventsId);
        window.log.info(`${DEBUG_TAG} destroyEvents playerEventsId: ${playerEventsId}`);
    }

    /**
     * 是否存在演示态的播放器实例
     */
    public static playerEventsExist(): boolean {
        if (!(window as any).MEGA_PLAYER_EVENTS) {
            return false;
        }
        const eventsKeys = [...(window as any).MEGA_PLAYER_EVENTS.keys()];
        return !!eventsKeys.find(key => key.startsWith('player'));
    }
}
